<template>
    <v-row justify="center">
        <!-- Change Password -->
        <v-dialog v-model="passwordDialog" persistent max-width="500px">
            <v-card>
                <v-card-title class="primary white--text">
                    Change Password
                    <v-spacer></v-spacer>
                </v-card-title>

                <v-card-text class="pt-5">
                    <v-form ref="passwordForm">
                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="editedItem.password"
                                    :counter="50"
                                    :rules="[rules.password]"
                                    label="New password*"
                                    :type="showpassword ? 'text' : 'password'"
                                    outlined
                                    class="subtitle-2"
                                    hint="At least 8 characters"
                                    :append-icon="
                                        showpassword
                                            ? 'visibility'
                                            : 'visibility_off'
                                    "
                                    @click:append="showpassword = !showpassword"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions class="pb-5 pr-5">
                    <v-spacer></v-spacer>
                    <v-btn
                        :disabled="isSavingPassword"
                        text
                        rounded
                        @click="closePasswordDialog"
                        >Cancel</v-btn
                    >
                    <v-btn
                        outlined
                        rounded
                        :loading="isSavingPassword"
                        color="primary"
                        @click="changePasswordConfirmed"
                        >Save</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Edit profile -->
        <v-dialog v-model="dialog" persistent max-width="600px">
            <v-card>
                <v-alert class="mb-0" v-if="error" tile type="error">{{
                    error
                }}</v-alert>

                <v-card-title class="primary white--text">
                    {{ formTitle }}
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="object && hasPermission('change_customuser')"
                        outlined
                        rounded
                        small
                        class="ml-2"
                        dark
                        @click="onChangePassword"
                        >Change Password</v-btn
                    >
                </v-card-title>

                <v-card-text class="pt-5">
                    <v-form ref="form">
                        <v-row>
                            <v-col cols="6">
                                <v-text-field
                                    v-model="editedItem.firstName"
                                    :rules="[
                                        rules.required,
                                        rules.maxLength(100)
                                    ]"
                                    label="First Name*"
                                    outlined
                                    class="subtitle-2"
                                    autocomplete="new-password"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="6">
                                <v-text-field
                                    v-model="editedItem.lastName"
                                    :rules="[
                                        rules.required,
                                        rules.maxLength(100)
                                    ]"
                                    label="Last Name*"
                                    outlined
                                    class="subtitle-2"
                                    autocomplete="new-password"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="editedItem.email"
                                    :rules="[
                                        rules.required,
                                        rules.maxLength(100)
                                    ]"
                                    label="Email*"
                                    type="email"
                                    outlined
                                    class="subtitle-2"
                                    autocomplete="new-password"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" v-if="!object">
                                <v-text-field
                                    v-model="editedItem.password"
                                    :rules="[rules.password]"
                                    label="Password*"
                                    :type="showpassword ? 'text' : 'password'"
                                    outlined
                                    class="subtitle-2"
                                    hint="At least 8 characters"
                                    :append-icon="
                                        showpassword
                                            ? 'visibility'
                                            : 'visibility_off'
                                    "
                                    @click:append="showpassword = !showpassword"
                                    autocomplete="new-password"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-switch
                                    v-model="editedItem.isAdmin"
                                    :label="`Administrator`"
                                    class="my-0 py-0"
                                    hide-details
                                ></v-switch>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions class="pb-5 pr-5">
                    <v-spacer></v-spacer>
                    <v-btn
                        :disabled="isSaving"
                        text
                        rounded
                        @click="closeUserDialog"
                        >Cancel</v-btn
                    >
                    <v-btn
                        outlined
                        rounded
                        :loading="isSaving"
                        color="primary"
                        @click="saveUser"
                        >Save</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import helper from "@/utils/helper.js";

export default {
    name: "tenant-user-edit-dialog",
    props: ["dialog", "object"],
    data() {
        return {
            error: null,
            isSaving: false,
            isSavingPassword: false,
            passwordDialog: false,

            showpassword: false,

            editedItem: {
                id: null,
                email: null,
                password: null,
                firstName: null,
                lastName: null,
                isAdmin: false
            },
            defaultItem: {
                id: null,
                email: null,
                password: null,
                firstName: null,
                lastName: null,
                isAdmin: false
            },
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                minLength: len => v =>
                    (v || "").length >= len ||
                    `Invalid character length, required ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long",
                password: value => {
                    const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/;
                    return (
                        pattern.test(value) ||
                        "Min. 8 characters with at least one capital letter, a number and a special character."
                    );
                }
            }
        };
    },
    computed: {
        formTitle() {
            return this.object ? "Edit User" : "New User";
        }
    },
    watch: {
        // reset form when dialog open or close
        dialog() {
            this.resetForm();
        }
    },
    created() {
        this.hasPermission = helper.hasPermission;
    },
    methods: {
        resetForm() {
            // reset form state
            this.error = null;
            this.isSaving = false;
            this.panel = 0;
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }

            // when editing, set form with object
            if (this.dialog && this.object) {
                this.editedItem.id = this.object.id;
                this.editedItem.email = this.object.email;
                this.editedItem.firstName = this.object.firstName;
                this.editedItem.lastName = this.object.lastName;
                this.editedItem.isAdmin = this.object.isAdmin;
            }
            // when new or closing, set form with default
            else {
                this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
            }
        },

        onChangePassword() {
            this.passwordDialog = true;
        },

        changePasswordConfirmed() {
            if (!this.$refs.passwordForm.validate()) {
                return;
            }

            // set form state
            this.error = null;
            this.isSavingPassword = true;

            // prepare api call payload
            var payload = {
                id: this.editedItem.id,
                newPassword: this.editedItem.password
            };

            this.$store
                .dispatch("user/changePassword", payload)
                .then(() => {
                    this.closePasswordDialog();
                })
                .catch(error => {
                    console.log(error);

                    this.error = error.graphQLErrors
                        .map(error => error.message)
                        .join(", ");
                })
                .finally(() => {
                    this.isSavingPassword = false;
                });
        },

        closePasswordDialog() {
            this.passwordDialog = false;
            this.editedItem.password = null;
            this.$refs.passwordForm.resetValidation();
        },

        closeUserDialog() {
            this.$emit("update:dialog", false);
            this.resetForm();
        },

        saveUser() {
            if (!this.$refs.form.validate()) {
                return;
            }

            // set form state
            this.error = null;
            this.isSaving = true;

            // prepare api call payload
            var payload = {
                email: this.editedItem.email,
                firstName: this.editedItem.firstName,
                lastName: this.editedItem.lastName,
                isAdmin: this.editedItem.isAdmin
            };

            // if editing
            if (this.object) {
                // prepare graphql mutation payload
                payload.id = this.editedItem.id;

                // update
                this.$store
                    .dispatch("user/updateUser", payload)
                    .then(response => {
                        // update parent object
                        this.$emit("update:object", response);
                        this.closeUserDialog();
                    })
                    .catch(error => {
                        this.error = error.graphQLErrors[0].message;
                        this.isSaving = false;
                    })
                    .finally(() => {});
            }
            // new
            else {
                payload.password = this.editedItem.password;

                this.$store
                    .dispatch("user/createUser", payload)
                    .then(response => {
                        this.$emit("item-created", response);
                        this.closeUserDialog();
                    })
                    .catch(error => {
                        console.log(error);

                        this.error = error.graphQLErrors
                            .map(error => error.message)
                            .join(", ");
                        this.isSaving = false;
                    })
                    .finally(() => {});
            }
        }
    }
};
</script>